import { FunctionComponent, useEffect, useState } from 'react'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import { Location, Navigate, useLocation } from 'react-router-dom'
import styles from './taskNote.module.scss'
import DaycareSelector from '@/components/DaycareSelector/daycareSelector.tsx'
import TaskNoteList from './taskNoteList/taskNoteList.tsx'
import { useQuery } from '@apollo/client'
import { GET_STAFF_LIST } from '@/modules/API/staff/staffHandler.ts'
import { StaffWithLinks } from '@/types/Staff.ts'
import Loading from '@/components/Loading/Loading.tsx'
import { GET_CHILD_LIST, GET_PARENT_LIST } from '@/modules/API/parent/parentHandler.ts'
import { Parent } from '@/types/Parent.ts'
import { ChildWithParents } from '@/types/Child.ts'
import { DaycareType } from '@/types/Daycare.ts'
import TaskNoteFilter from './taskNoteFilter/taskNoteFilter.tsx'
import { TaskNote, TaskNoteStatus, TaskNoteType } from '@/types/TaskNote.ts'
import Desktop from '@/modules/desktopMobile/desktop.tsx'

const TaskNotePage: FunctionComponent = () => {
    const { user } = useAuth()

    const urlEvents: Location = useLocation()
    const paramEvents: URLSearchParams = new URLSearchParams(urlEvents.search)

    const fromDate: string | null = paramEvents.get('fromDate')
    const toDate: string | null = paramEvents.get('toDate')

    const parentId: string | null = paramEvents.get('parentId')
    const childId: string | null = paramEvents.get('childId')
    const staffId: string | null = paramEvents.get('staffId')

    const [staffList, setStaffList] = useState<StaffWithLinks[]>([])
    const [parentsList, setParentsList] = useState<Parent[]>([])
    const [childsList, setChildsList] = useState<ChildWithParents[]>([])

    const { loading: loadingStaffs, data: dataStaffs } = useQuery(GET_STAFF_LIST, {
        variables: {
            daycareId: Number(user?.currentDaycare?.id)
        }
    })

    useEffect(() => {
        if (dataStaffs) {
            setStaffList(dataStaffs.getStaffList)
        }
    }, [dataStaffs])

    const { loading: loadingParentList, data: dataParentList } = useQuery(GET_PARENT_LIST, {
        variables: {
            daycare_id: Number(user?.currentDaycare?.id)
        }
    })

    useEffect(() => {
        if (dataParentList && dataParentList.getParentList) {
            setParentsList(dataParentList.getParentList.filter((parent: Parent): boolean => parent.id !== 0))
        }
    }, [dataParentList])

    const { loading: loadingChildList, data: dataChildList } = useQuery(GET_CHILD_LIST, {
        variables: {
            daycareId: Number(user?.currentDaycare?.id)
        }
    })

    useEffect(() => {
        if (dataChildList && dataChildList.getChildList) {
            setChildsList(dataChildList.getChildList)
        }
    }, [dataChildList])

    const [typeFilters, setTypeFilters] = useState<string[]>(Object.keys(TaskNoteType))
    const [statusFilters, setStatusFilters] = useState<string[]>([])

    const [countsByType, setCountsByType] = useState<Record<string, number>>({})
    const [countsByStatus, setCountsByStatus] = useState<Record<string, number>>({})

    function handleDataLoaded(taskNotes: TaskNote[]): void {
        const countsType: Record<string, number> = taskNotes.reduce(
            (acc: Record<string, number>, note: TaskNote): Record<string, number> => {
                acc[note.type] = (acc[note.type] ?? 0) + 1
                return acc
            },
            {} as Record<string, number>
        )
        setCountsByType(countsType)

        const countsStatus: Record<string, number> = taskNotes.reduce(
            (acc: Record<string, number>, note: TaskNote): Record<string, number> => {
                if (
                    note.status === TaskNoteStatus.done &&
                    (note.type === TaskNoteType.appointmentInPerson ||
                        note.type === TaskNoteType.appointmentVideoconference)
                ) {
                    acc[note.status + ' ' + note.type] = (acc[note.status + ' ' + note.type] ?? 0) + 1
                } else if (note.status) {
                    acc[note.status] = (acc[note.status] ?? 0) + 1
                }
                return acc
            },
            {} as Record<string, number>
        )
        setCountsByStatus(countsStatus)
    }

    function resetFilter(): void {
        setTypeFilters(Object.keys(TaskNoteType))
        setStatusFilters([])
    }

    useEffect(() => {
        setStatusFilters([])
        setStatusFilters(getStatusFromType(typeFilters))
    }, [typeFilters])

    return (
        <div className={styles.PagePosition}>
            {(loadingStaffs || loadingParentList || loadingChildList) && <Loading overall />}
            {user?.role && user.currentDaycare && user?.currentDaycareRights.canReadTaskNote ? (
                <>
                    <div className={styles.HeaderRow}>
                        <div className={styles.MultipleSections}>
                            <DaycareSelector />
                        </div>
                        <Desktop>
                            <TaskNoteFilter
                                typeFilters={typeFilters}
                                setTypeFilters={setTypeFilters}
                                statusFilters={statusFilters}
                                setStatusFilters={setStatusFilters}
                                countsByType={countsByType}
                                countsByStatus={countsByStatus}
                            />
                        </Desktop>
                    </div>
                    <div className={styles.TaskNotePlacement}>
                        <TaskNoteList
                            fromDate={fromDate}
                            toDate={toDate}
                            parentId={parentId}
                            childId={childId}
                            staffId={staffId}
                            staffList={staffList}
                            childList={childsList}
                            parentsList={parentsList}
                            isRpeOrLaep={
                                user.currentDaycare.type === DaycareType.rpe ||
                                user.currentDaycare.type === DaycareType.laep
                            }
                            daycareId={user.currentDaycare.id}
                            canUpdate={user.currentDaycareRights.canWriteTaskNote}
                            typeFilters={typeFilters}
                            statusFilters={statusFilters}
                            setTypeFilters={setTypeFilters}
                            handleDataLoaded={handleDataLoaded}
                            resetFilter={resetFilter}
                        />
                    </div>
                </>
            ) : (
                <Navigate to='/' replace />
            )}
        </div>
    )
}

export default TaskNotePage

function getStatusFromType(types: string[]): string[] {
    const status: string[] = []

    if (types.includes(TaskNoteType.task)) {
        status.push(TaskNoteStatus.todo, TaskNoteStatus.inProgress, TaskNoteStatus.done)
    }
    if (types.includes(TaskNoteType.call)) {
        status.push(TaskNoteStatus.connected, TaskNoteStatus.noAnswer, TaskNoteStatus.leftMessage)
    }
    if (types.includes(TaskNoteType.appointmentInPerson)) {
        status.push(
            TaskNoteStatus.schedule,
            TaskNoteStatus.done + ' ' + TaskNoteType.appointmentInPerson,
            TaskNoteStatus.missed,
            TaskNoteStatus.cancelled
        )
    }
    if (types.includes(TaskNoteType.appointmentVideoconference)) {
        status.push(
            TaskNoteStatus.schedule,
            TaskNoteStatus.done + ' ' + TaskNoteType.appointmentVideoconference,
            TaskNoteStatus.missed,
            TaskNoteStatus.cancelled
        )
    }

    return status
}
